import { useCopilotRecommendation } from "@/api/copilot/queries";
import { StarIcon } from "@/assets/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TextSkeleton } from "@/features/projects/components/text-skeleton";
import { useSegment } from "@/hooks/use-segment";
import { ReportType, useReportsContext } from "@/features/reports/contexts/reports-context";
import { useEffect } from "react";
import Markdown from "react-markdown";
import { AttentionMetric } from "@/api/studio/insights/types/metrics.type";
import { XMarkIcon } from "@heroicons/react/24/outline";

/* Prop Types */
interface CopilotProps {
  projectId: string;
  studyId: string;
  assetId: string;
  reportType: ReportType;
  metric?: AttentionMetric; // Attention reports only
  studyType?: "asset" | "scene"; // Scene studies require type=scene on the copilot endpoint
  onClose?: () => void; // Optional callback to close copilot (for non-context usage)
}

/*
 * Copilot component
 *
 * Display the copilot recommendation text for different report types.
 */
export const Copilot = ({ projectId, studyId, assetId, reportType, metric, studyType, onClose }: CopilotProps) => {
  /* Context */
  const { state, actions } = useReportsContext();

  const emotionMetric = state.emotionMetric;
  const memoryMetric = state.memoryMetric;

  /* Handler */
  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      actions.setShowCopilot(false);
    }
  };

  /* Queries */
  const {
    data: copilot,
    isLoading: isCopilotLoading,
    isError: isCopilotError,
  } = useCopilotRecommendation(projectId, studyId, assetId, reportType as "attention" | "memory" | "emotion", metric, studyType);

  /* Hooks */
  const { segmentTrackEvent } = useSegment();

  /* Side effects */
  useEffect(() => {
    // Segment Tracking
    if (isCopilotLoading)
      segmentTrackEvent("Asset Report Copilot Requested", {
        assetId,
        projectId,
        studyId,
        reportType,
        metric: reportType === "emotion" ? emotionMetric : reportType === "memory" ? memoryMetric : metric,
      });
  }, [isCopilotLoading]);

  /* Render */
  return (
    <div data-theme="light" className="flex text-white gap-y-4 flex-col min-w-[364px] max-w-[364px] bg-linear-to-br from-violet-800 to-pink-600 rounded-2xl p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <StarIcon className="size-5 mr-2" />
          <h3>Copilot</h3>
          <Badge className="ml-2 text-xs border-none py-1 text-white bg-black/40">Beta</Badge>
        </div>
        <XMarkIcon onClick={handleClose} className="size-5 cursor-pointer" />
      </div>

      {/* Error */}
      {isCopilotError && (
        <div className="flex flex-col gap-y-4 h-full">
          <p>Looks like Copilot is not available right now. Try refreshing the page or contact us if this issue persists</p>
          <a href="mailto:support@dragonflyai.co">
            <Button className="btn-outline self-start text-white">Contact support</Button>
          </a>
          <p className="text-xs pt-3 mt-auto border-t-white border-t">Copilot cannot guarantee factual accuracy and can make mistakes. Its output quality might vary.</p>
        </div>
      )}

      {/* Loading */}
      {isCopilotLoading && (
        <div className="flex flex-col gap-y-3 h-full">
          {Array.from({ length: 6 }).map((_, i) => (
            <TextSkeleton key={i} className="opacity-60" variant={"h1"} />
          ))}
          <p className="text-xs pt-3 mt-auto border-t-white border-t">Copilot cannot guarantee factual accuracy and can make mistakes. Its output quality might vary.</p>
        </div>
      )}

      {/* recommendation */}
      {copilot && !isCopilotLoading && (
        <div className="overflow-y-scroll">
          <div className="text-sm pb-4">
            <Markdown
              components={{
                h2: ({ children }) => <h2 className="text-lg font-bold pb-1">{children}</h2>,
                p: ({ children }) => <p className="whitespace-pre-wrap pb-4">{children}</p>,
                ul: ({ children }) => <ul className="list-disc ml-5 pb-4">{children}</ul>,
                li: ({ children }) => <li className="text-base mb-1">{children}</li>,
              }}
            >
              {copilot.recommendation}
            </Markdown>
          </div>
          <p className="text-xs pt-3 border-t-white border-t">Copilot cannot guarantee factual accuracy and can make mistakes. Its output quality might vary.</p>
        </div>
      )}
    </div>
  );
};
